import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { fetchScenarioTelemetry, fetchScenarioTelemetryEvents } from '../api';
import ScenarioEventGrid, { parseMetadata } from './ScenarioEventGrid';

const TERMINAL = new Set(['COMPLETED', 'COMPLETED WITH ERRORS', 'FAILED', 'SKIPPED']);
const STATUS = {
  'IN PROGRESS': ['In progress', 'info'],
  COMPLETED: ['Completed', 'success'],
  'COMPLETED WITH ERRORS': ['Completed with errors', 'warning'],
  FAILED: ['Failed', 'error'],
  SKIPPED: ['Skipped', 'default'],
};
const STAGES = {
  starting: 'Starting', preparation: 'Preparation', adco: 'ADCO processing',
  intex: 'INTEX processing', position_collection: 'Collecting positions',
  scenario_preparation: 'Preparing scenarios', scenario_calculation: 'Scenario calculation',
  book_containers: 'Saving book containers', persistence: 'Preparing results for storage',
  snowflake_save: 'Saving to Snowflake', exception_persistence: 'Saving exception records',
  finished: 'Finished',
};
const label = value => typeof value === 'string' ? value.replace(/_/g, ' ') : 'Unavailable';
const timestamp = value => value ? Date.parse(value) : NaN;
const formatTime = value => Number.isFinite(timestamp(value))
  ? new Date(value).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    second: '2-digit', timeZoneName: 'short',
  }) : 'Unavailable';
const duration = milliseconds => {
  if (!Number.isFinite(milliseconds)) return 'Unavailable';
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  return `${hours ? `${hours}h ` : ''}${minutes}m ${seconds % 60}s`;
};
const count = value => Number.isInteger(value) && value >= 0;

function Progress({ meta }) {
  const group = count(meta.total_chunks) ? 'chunks' : count(meta.total_books) ? 'books' : null;
  if (!group || !count(meta[`processed_${group}`])) return null;
  return <Typography variant="body2">
    {meta[`processed_${group}`]} of {meta[`total_${group}`]} {group} processed
    {count(meta[`failed_${group}`]) ? ` · ${meta[`failed_${group}`]} failed` : ''}
  </Typography>;
}

function TraceToggle({ trace, name }) {
  const [open, setOpen] = useState(false);
  return <Box mt={0.5}>
    <Button size="small" onClick={() => setOpen(value => !value)} aria-expanded={open}>
      {open ? 'Hide stack trace' : 'Show stack trace'}
    </Button>
    {open && <Box component="pre" aria-label={name ? `Stack trace for ${name}` : 'Stack trace'} sx={{
      m: 0, p: 1, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre', fontFamily: 'monospace',
      fontSize: 12, bgcolor: 'action.hover', borderRadius: 1,
    }}>{trace}</Box>}
  </Box>;
}

const objects = value => Array.isArray(value) ? value.filter(item => item && typeof item === 'object') : [];
const text = value => typeof value === 'string' && value ? value : null;

function FailureDetail({ meta, status }) {
  const message = text(meta.error_message);
  const trace = text(meta.traceback);
  const positions = objects(meta.position_errors);
  const chunks = objects(meta.chunk_errors);
  // Engines send a sample; the counts say how many failures it left out.
  const totalPositions = (count(meta.failed_positions) ? meta.failed_positions : 0)
    + (count(meta.invalid_analytics) ? meta.invalid_analytics : 0);
  const totalChunks = count(meta.tracked_error_count) ? meta.tracked_error_count : 0;
  if (!message && !trace && !positions.length && !chunks.length) {
    return status === 'FAILED' || status === 'COMPLETED WITH ERRORS'
      ? <Typography variant="body2">Full failure messages are not available in telemetry.</Typography> : null;
  }
  return <>
    {message && <Typography sx={{ overflowWrap: 'anywhere' }}>Error: {message}</Typography>}
    {!!positions.length && <Box mt={1}>
      <Typography variant="body2">Failed positions:</Typography>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {positions.map((item, index) => <Typography component="li" variant="body2" key={index}
          sx={{ overflowWrap: 'anywhere' }}>
          {[item.cusip, item.lot_id, item.book].map(value => value ?? '—').join(' / ')}: {String(item.error_message ?? 'Unavailable')}
        </Typography>)}
      </Box>
      {totalPositions > positions.length && <Typography variant="body2">+{totalPositions - positions.length} more</Typography>}
    </Box>}
    {!!chunks.length && <Box mt={1}>
      <Typography variant="body2">Failed chunks:</Typography>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {chunks.map((item, index) => {
          const cusips = Array.isArray(item.cusips) ? item.cusips.map(String) : [];
          const hidden = count(item.cusip_count) ? item.cusip_count - cusips.length : 0;
          const name = item.chunk_id != null ? `chunk ${item.chunk_id}` : `failure ${index + 1}`;
          return <Typography component="li" variant="body2" key={index} sx={{ overflowWrap: 'anywhere' }}>
            {[label(item.stage), text(item.book), name,
              cusips.length ? cusips.join(', ') + (hidden > 0 ? ` +${hidden} more` : '') : null]
              .filter(Boolean).join(' · ')}: {[text(item.error_type), text(item.error_message)].filter(Boolean).join(': ') || 'Unavailable'}
            {text(item.traceback) && <TraceToggle trace={item.traceback} name={name} />}
          </Typography>;
        })}
      </Box>
      {totalChunks > chunks.length && <Typography variant="body2">+{totalChunks - chunks.length} more</Typography>}
    </Box>}
    {trace && <TraceToggle trace={trace} />}
  </>;
}

function EventHistory({ configId, runId, lastEventTime }) {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setData(null);
    setError(false);
    fetchScenarioTelemetryEvents(configId, runId, offset, controller.signal)
      .then(response => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(response.data?.events)) throw new Error('Invalid event history');
        setData(response.data);
      })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [open, configId, runId, offset, lastEventTime, retry]);
  return <Box mt={2}>
    <Button onClick={() => setOpen(value => !value)} aria-expanded={open}>
      {open ? 'Hide event history' : 'View event history'}
    </Button>
    {open && <>
      <Typography variant="body2">Recorded telemetry events, oldest first. This is not the full worker log.</Typography>
      {error ? <Alert severity="warning" action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>
        Event history is temporarily unavailable.
      </Alert> : !data ? <CircularProgress size={20} aria-label="Loading event history" /> : <>
        {!data.events.length ? <Typography>No events recorded for this run.</Typography> :
          <ScenarioEventGrid events={data.events} offset={offset} runId={runId} formatTime={formatTime} />}
        <Box display="flex" alignItems="center" gap={1}>
          <Button disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - 200))}>Previous events</Button>
          <Typography variant="body2">Page {offset / 200 + 1}</Typography>
          <Button disabled={!data.has_more} onClick={() => setOffset(value => value + 200)}>Next events</Button>
          <Button onClick={() => setRetry(value => value + 1)}>Refresh events</Button>
        </Box>
      </>}
    </>}
  </Box>;
}

export default function ExecutionStatus({
  configId, submissionVersion = 0, dismissedSubmission = 0, onDismissSubmission,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clock, setClock] = useState(null);
  const [now, setNow] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const request = useRef(null);
  const generation = useRef(0);
  const rerun = useRef(false);

  const refresh = useCallback(async () => {
    if (!configId || document.hidden) return;
    if (request.current) {
      rerun.current = true;
      return;
    }
    const controller = new AbortController();
    const version = generation.current;
    request.current = controller;
    setLoading(true);
    try {
      const response = await fetchScenarioTelemetry(configId, controller.signal);
      if (controller.signal.aborted || version !== generation.current) return;
      const payload = response.data;
      if (!Array.isArray(payload?.executions)) throw new Error('Invalid telemetry response');
      setRows(payload.executions);
      const serverTime = timestamp(payload.server_time);
      setClock(Number.isFinite(serverTime) ? { serverTime, receivedAt: performance.now() } : null);
      setNow(Number.isFinite(serverTime) ? serverTime : null);
      setError(null);
    } catch (err) {
      if (!controller.signal.aborted && version === generation.current) {
        setError('Telemetry is unavailable. Previously loaded information may be out of date.');
      }
    } finally {
      if (request.current === controller) request.current = null;
      if (version === generation.current && !controller.signal.aborted) {
        setLoading(false);
        if (rerun.current) {
          rerun.current = false;
          refresh();
        }
      }
    }
  }, [configId]);

  useEffect(() => {
    generation.current += 1;
    setRows([]);
    setError(null);
    setClock(null);
    setNow(null);
    refresh();
    const interval = setInterval(refresh, 30000);
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      generation.current += 1;
      request.current?.abort();
      request.current = null;
      rerun.current = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  useEffect(() => { if (submissionVersion) refresh(); }, [submissionVersion, refresh]);
  useEffect(() => {
    if (!clock) return undefined;
    const tick = () => setNow(clock.serverTime + performance.now() - clock.receivedAt);
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [clock]);

  const toggle = runId => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(runId)) next.delete(runId); else next.add(runId);
    return next;
  });

  return <Box component="section" aria-label="Execution Status">
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <Typography variant="h5">Execution Status</Typography>
      {configId && <Button onClick={refresh} disabled={loading}>Refresh</Button>}
      {loading && <CircularProgress size={20} aria-label="Loading telemetry" />}
    </Box>
    {!configId ? <Typography>Select a configuration to view execution status.</Typography> : <>
      {submissionVersion > dismissedSubmission && <Alert severity="info"
        onClose={onDismissSubmission ? () => onDismissSubmission(submissionVersion) : undefined} sx={{ mb: 2 }}>
        Submitted—awaiting telemetry. Executions below are reported independently;
        they have not been matched to this submission.
      </Alert>}
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {!rows.length && !loading && !error && <Typography>No telemetry recorded for this configuration.</Typography>}
      {!!rows.length && <>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Active executions and the latest 50 outcomes. Updates reflect reported progress, not a heartbeat.
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="Scenario executions" sx={{ width: 'max-content', minWidth: '100%',
            '& > thead > tr > th, & > tbody > tr > td:not([colspan])': {
              width: '1%', whiteSpace: 'nowrap', verticalAlign: 'top',
            },
            '& > thead > tr > th:last-child, & > tbody > tr > td:last-child:not([colspan])': {
              width: 'auto',
            },
          }}>
            <TableHead><TableRow>
              {['Scenario / type', 'Book scope', 'Status', 'Stage / progress', 'Elapsed', 'Last update / finished', 'Details']
                .map(title => <TableCell key={title}>{title}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>{rows.map(row => {
              const parsed = parseMetadata(row.META);
              const meta = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
              const [status, color] = (Object.prototype.hasOwnProperty.call(STATUS, row.STATUS) ? STATUS[row.STATUS] : null) || [row.STATUS || 'Unknown', 'default'];
              const end = TERMINAL.has(row.STATUS) ? timestamp(row.EVENT_TIME) : now ?? NaN;
              const elapsed = end - timestamp(row.STARTED_AT);
              const age = (now ?? NaN) - timestamp(row.EVENT_TIME);
              const scope = typeof meta.book_scope === 'string' && meta.book_scope ? meta.book_scope : 'All / unspecified';
              return <React.Fragment key={row.RUN_ID}>
                <TableRow>
                  <TableCell>{typeof meta.scenario === 'string' ? meta.scenario : 'Unavailable'}
                    <Typography variant="caption">{typeof meta.flag === 'string' ? ` · ${meta.flag}` : ''}</Typography>
                  </TableCell>
                  <TableCell>{scope}</TableCell>
                  <TableCell><Chip label={status} color={color} size="small" /></TableCell>
                  <TableCell>{row.STATUS === 'COMPLETED' ? 'Complete' : <>
                    {Object.prototype.hasOwnProperty.call(STAGES, meta.stage) ? STAGES[meta.stage] : label(meta.stage)}
                    <Progress meta={meta} />
                  </>}</TableCell>
                  <TableCell>{duration(elapsed)}</TableCell>
                  <TableCell>{TERMINAL.has(row.STATUS)
                    ? <>Finished<Typography variant="body2">{formatTime(row.EVENT_TIME)}</Typography></>
                    : Number.isFinite(age) ? `${duration(age)} ago` : 'Unavailable'}</TableCell>
                  <TableCell><Button size="small" onClick={() => toggle(row.RUN_ID)}
                    aria-expanded={expanded.has(row.RUN_ID)}
                    aria-label={`Details for ${row.RUN_ID}`}>Details</Button></TableCell>
                </TableRow>
                {expanded.has(row.RUN_ID) && <TableRow><TableCell colSpan={7}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Run ID: {row.RUN_ID}</Typography>
                  <Typography variant="body2">Started: {formatTime(row.STARTED_AT)} · {TERMINAL.has(row.STATUS) ? 'Finished' : 'Last event'}: {formatTime(row.EVENT_TIME)}</Typography>
                  {typeof meta.error_type === 'string' && <Typography>Error type: {meta.error_type}</Typography>}
                  {typeof meta.reason === 'string' && <Typography>Reason: {label(meta.reason)}</Typography>}
                  {count(meta.tracked_error_count) && <Typography>Tracked errors: {meta.tracked_error_count}</Typography>}
                  {Array.isArray(meta.error_stages) && meta.error_stages.length > 0 &&
                    <Typography>Error stages: {meta.error_stages.map(label).join(', ')}</Typography>}
                  <FailureDetail meta={meta} status={row.STATUS} />
                  <EventHistory configId={configId} runId={row.RUN_ID} lastEventTime={row.EVENT_TIME} />
                </TableCell></TableRow>}
              </React.Fragment>;
            })}</TableBody>
          </Table>
        </TableContainer>
      </>}
    </>}
  </Box>;
}
